import { useEffect, useRef, useState } from 'react';
import { Code2, Cpu, ExternalLink, Maximize2, Save, Wrench } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { partsFor } from '../../data/parts';
import { loadJSON, saveJSON, storageKeys, SANDBOX_CODE_KEY } from '../../storage';

interface Props {
  comp: Competition;
}

/** The real wiring + code workbench lives in the standalone simulator page. */
const WORKBENCH_PATH = '/src/client/pages/index.html';

export default function WireCodeTab({ comp }: Props) {
  const { required, optional } = partsFor(comp.id);
  const allowed = [...required, ...optional];
  const workbenchUrl = `${WORKBENCH_PATH}?arena=${comp.id}`;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [firmware, setFirmware] = useState<string>(() =>
    loadJSON<string>(storageKeys.firmware(comp.id), ''),
  );
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Hand the workbench a scope so it knows which competition/parts/limits apply.
  // (Read by the sandbox today as guidance; consumed by the sim in Phase 2.)
  useEffect(() => {
    saveJSON(storageKeys.scope(), {
      competitionId: comp.id,
      competitionName: comp.name,
      allowedPartIds: allowed.map((p) => p.id),
      allowedFileNames: allowed.flatMap((p) => p.fileNames ?? []),
      rulePack: comp.rulePack,
      updatedAt: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp.id]);

  // Pull the code the workbench auto-saved and store it as THIS competition's firmware.
  const captureFirmware = () => {
    const code = loadJSON<string>(SANDBOX_CODE_KEY, '');
    if (!code) return;
    saveJSON(storageKeys.firmware(comp.id), code);
    setFirmware(code);
    setSavedAt(Date.now());
  };

  const openFullscreen = () => window.open(workbenchUrl, '_blank', 'noopener');

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <h3 className="flex items-center gap-2 text-base font-bold text-white">
              <Wrench className={`size-4 ${comp.text}`} />
              Wire &amp; code your {comp.name}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
              The Build tab tunes your robot with sliders. Here you build it for real: drag the allowed
              parts onto the board, wire the pins, and program the behaviour in Arduino C++ with the
              same engine the Sandbox uses. Your code is auto-saved as you type — use{' '}
              <span className="font-semibold text-slate-200">Save as competition firmware</span> to lock
              it to this competition.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={captureFirmware}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-300 ring-1 ring-white/10 transition hover:text-white hover:ring-white/25"
            >
              <Save className="size-3.5" />
              Save as competition firmware
            </button>
            <button
              type="button"
              onClick={openFullscreen}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${comp.button}`}
            >
              <Maximize2 className="size-3.5" />
              Open full screen
            </button>
          </div>
        </div>

        {/* Firmware status */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 font-semibold text-slate-300">
            <Cpu className="size-3.5" />
            {firmware
              ? `Firmware saved for this competition (${firmware.split('\n').length} lines)`
              : 'No firmware saved yet for this competition'}
          </span>
          {savedAt && <span className="text-emerald-400">✓ Saved just now</span>}
        </div>
      </div>

      {/* Allowed parts scope */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <h4 className="text-sm font-bold uppercase tracking-wide text-slate-400">
          Parts allowed in this competition
        </h4>
        <p className="mt-1 text-xs text-slate-500">
          Build with these in the workbench. See the Parts tab for wiring notes and tips.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {allowed.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-2.5 py-1.5 text-xs font-medium text-slate-200 ring-1 ring-white/10"
            >
              <Code2 className="size-3 text-slate-500" />
              {p.name}
              {p.optional && <span className="text-[10px] text-slate-500">optional</span>}
            </span>
          ))}
        </div>
      </div>

      {/* Embedded workbench */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-navy-950/60">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-400">
            <Wrench className="size-3.5" />
            Wiring &amp; Code Workbench
          </span>
          <a
            href={workbenchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 transition hover:text-white"
          >
            Open in new tab
            <ExternalLink className="size-3" />
          </a>
        </div>
        <iframe
          ref={iframeRef}
          src={workbenchUrl}
          title={`${comp.name} wiring and code workbench`}
          className="h-[78vh] w-full border-0 bg-white"
        />
      </div>
      <p className="text-center text-xs text-slate-500">
        Tip: the workbench is a full IDE — for serious building use{' '}
        <button onClick={openFullscreen} className="font-semibold text-slate-300 underline">
          full screen
        </button>
        , then come back and save your firmware.
      </p>
    </div>
  );
}
